import { useCallback, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AnalyticsEvents, track } from '../services/AnalyticsService';
import { Building2, Eye, EyeOff } from 'lucide-react';
import TurnstileWidget, { isCaptchaEnabled } from '../components/TurnstileWidget';
import { suggestEmail } from '../utils/emailSuggestion';

export default function RegisterPage() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const [captchaFailed, setCaptchaFailed] = useState(false);
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const startedTracked = useRef(false);

  // Fired once, on the first keystroke, so the funnel separates "looked at the form" from "tried to fill it in".
  const trackStarted = () => {
    if (startedTracked.current) return;
    startedTracked.current = true;
    track(AnalyticsEvents.SignUpStarted);
  };

  const handleCaptchaToken = useCallback((token: string) => {
    setCaptchaToken(token);
    if (token) setCaptchaFailed(false);
  }, []);

  // Without this the submit button just stays disabled and the visitor has no idea why.
  const handleCaptchaError = useCallback(() => {
    setCaptchaFailed(true);
    track(AnalyticsEvents.SignUpCaptchaFailed);
  }, []);

  const checkEmail = () => {
    const suggestion = suggestEmail(email);
    setEmailSuggestion(suggestion);
    if (suggestion) track(AnalyticsEvents.EmailSuggestionShown);
  };

  const acceptEmailSuggestion = () => {
    if (!emailSuggestion) return;
    setEmail(emailSuggestion);
    setEmailSuggestion(null);
    track(AnalyticsEvents.EmailSuggestionAccepted);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 10) {
      const message = 'Lösenordet måste vara minst 10 tecken';
      setError(message);
      track(AnalyticsEvents.SignUpFailed, { reason: message });
      return;
    }
    setLoading(true);
    try {
      await signUp(email, password, fullName, captchaToken);
      navigate('/organizations');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Registrering misslyckades');
      setCaptchaToken('');
      setCaptchaKey(k => k + 1);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-teal-700 mb-4">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Skapa konto</h1>
          <p className="text-gray-500 mt-1">Börja hantera din samfällighetsförening</p>
          <p className="text-sm font-medium text-teal-800 mt-3">Gratis första året · Inget kreditkort · Ingen bindningstid</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 space-y-5">
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm border border-red-100">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="register-fullname" className="block text-sm font-medium text-gray-700 mb-1.5">Fullständigt namn</label>
            <input
              id="register-fullname"
              type="text"
              value={fullName}
              onChange={e => {
                setFullName(e.target.value);
                trackStarted();
              }}
              required
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
              placeholder="Förnamn Efternamn"
            />
          </div>

          <div>
            <label htmlFor="register-email" className="block text-sm font-medium text-gray-700 mb-1.5">E-post</label>
            <input
              id="register-email"
              type="email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                setEmailSuggestion(null);
                trackStarted();
              }}
              onBlur={checkEmail}
              required
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
              placeholder="din@epost.se"
            />
            {emailSuggestion && (
              <p className="mt-1.5 text-sm text-amber-700">
                Menade du{' '}
                <button type="button" onClick={acceptEmailSuggestion} className="font-semibold underline hover:text-amber-900">
                  {emailSuggestion}
                </button>
                ?
              </p>
            )}
          </div>

          <div>
            <label htmlFor="register-password" className="block text-sm font-medium text-gray-700 mb-1.5">Lösenord</label>
            <div className="relative">
              <input
                id="register-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  trackStarted();
                }}
                required
                minLength={10}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all pr-10"
                placeholder="Minst 10 tecken"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Dölj lösenord' : 'Visa lösenord'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <TurnstileWidget key={captchaKey} onToken={handleCaptchaToken} onError={handleCaptchaError} />
          {captchaFailed && (
            <p className="text-center text-sm text-red-700">
              Verifieringen kunde inte laddas. Ladda om sidan och försök igen.
            </p>
          )}

          <button
            type="submit"
            disabled={loading || (isCaptchaEnabled() && !captchaToken)}
            className="w-full py-2.5 px-4 bg-teal-700 hover:bg-teal-800 text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Skapar konto...' : 'Skapa konto'}
          </button>

          <p className="text-center text-sm text-gray-500">
            Har du redan ett konto?{' '}
            <Link to="/login" className="text-teal-700 hover:text-teal-800 font-medium">
              Logga in
            </Link>
          </p>

          <p className="text-center text-sm text-gray-500">
            Vill du titta först?{' '}
            <Link to="/demo" className="text-teal-700 hover:text-teal-800 font-medium">
              Prova testföreningen
            </Link>{' '}
            – ingen registrering.
          </p>
        </form>
      </div>
    </div>
  );
}
